<template>
  <div class="print-root">
    <table class="print-table">
      <thead>
        <tr>
          <th :colspan="columnCount" class="title">
            成人式当日一覧 年度: {{ nendo }} 準備場所: {{ placeName }}
          </th>
        </tr>
        <tr class="thead-row">
          <th class="col time">開始時間</th>
          <th class="col date">契約日</th>
          <th class="col slip">伝票No</th>
          <th class="col type">区分</th>
          <th class="col name">氏名</th>
          <th class="col parent">保護者名</th>
          <th class="col address">住所</th>
          <th class="col tel">電話番号</th>
          <th class="col money">金額</th>
          <th class="col memo">当日詳細/備考</th>
          <th class="col staff">営業担当</th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(row, idx) in rows"
          :key="idx"
          :class="{ 'break-before': idx > 0 && (splitByKeyword ? (row.teacher !== rows[idx - 1].teacher) : (row.time !== rows[idx - 1].time)) }"
          style="page-break-inside: avoid;"
        >
          <td class="time">
            <div class="time-text">{{ toTimeString(row.time) }}</div>
            <div class="teacher">{{ row.ev ? getPlaceKeyword(row.ev) : (row.teacher || '') }}</div>
          </td>
          <td class="date">{{ row.ev ? formatDate(getReceipt(row.ev)) : '' }}</td>
          <td class="slip">{{ row.ev ? getSlipNumber(row.ev) : '' }}</td>
          <td class="type">{{ row.ev ? getEventTypeText(row.ev) : '' }}</td>
          <td class="name">
            <div class="furigana">{{ row.ev ? getFurigana(row.ev) : '' }}</div>
            <div class="fullname">{{ row.ev ? getCustomerName(row.ev) : '' }}</div>
          </td>
          <td class="parent">{{ row.ev ? getParentName(row.ev) : '' }}</td>
          <td class="address">
            <div class="postal">{{ row.ev ? getPostal(row.ev) : '' }}</div>
            <div class="address-text">{{ row.ev ? getAddress(row.ev) : '' }}</div>
          </td>
          <td class="tel">
            <div>家: {{ row.ev ? getTelHome(row.ev) : '' }}</div>
            <div>娘: {{ row.ev ? getTelDaughter(row.ev) : '' }}</div>
            <div>保: {{ row.ev ? getTelMother(row.ev) : '' }}</div>
          </td>
          <td class="money">{{ row.ev ? formatMoney(getKingaku(row.ev)) : '' }}</td>
          <td class="memo" style="font-size:0.9em;">{{ row.ev ? formatMemo(row.ev) : '' }}</td>
          <td class="staff">{{ row.ev ? getStaffName(row.ev) : '' }}</td>
        </tr>
      </tbody>
    </table>
  </div>
  
</template>

<script>
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../plugins/firebase';
import moment from 'moment';
import moji from 'moji';

export default {
  name: 'CelemonyPrint',
  data: () => ({
    nendo: '',
    onTheDay: -1,
    placeId: 0,
    placeName: '',
    events: [],
    places: [],
    columnCount: 11,
    slotMap: null,
    splitByKeyword: false,
  }),
  computed: {
    sortedEvents() {
      const clone = this.events.slice();
      const toNum = (v) => {
        const n = Number(v);
        return Number.isFinite(n) ? n : Number.MAX_SAFE_INTEGER;
      };
      const toStr = (v) => (v == null ? '' : String(v));
      const getTeacher = (ev) => toStr(ev?.celemony?.place_keyword).trim();
      return clone.sort((a, b) => {
        if (this.splitByKeyword) {
          const ta = getTeacher(a);
          const tb = getTeacher(b);
          if (ta !== tb) return ta.localeCompare(tb, 'ja');
        }
        const at = toNum(a?.celemony?.onTheDayTime);
        const bt = toNum(b?.celemony?.onTheDayTime);
        if (at !== bt) return at - bt;
        const ar = toNum(a?.receipt);
        const br = toNum(b?.receipt);
        return ar - br;
      });
    },
    rows() {
      const sorted = this.sortedEvents;
      if (this.splitByKeyword) {
        // 先生>時間>契約日の順にソート済み。パディングはせず、そのまま出力
        return sorted.map(ev => ({
          time: this.getOnTheDayTime(ev),
          teacher: (ev?.celemony?.place_keyword || '').toString().trim(),
          ev,
        }));
      }
      // 枠が未設定でも、時間ごとに空き枠行を出す
      const hasSlots = this.slotMap && Object.keys(this.slotMap).length > 0;
      const eventsByTime = {};
      sorted.forEach(ev => {
        const t = this.getOnTheDayTime(ev);
        if (!eventsByTime[t]) eventsByTime[t] = [];
        eventsByTime[t].push(ev);
      });
      const out = [];
      if (!hasSlots) {
        // 枠情報がない場合は、各出現時間ごとに既定枠数(20)まで空行を出力
        const DEFAULT_CAPACITY = 20;
        const timeKeys = Object.keys(eventsByTime).map(k => parseInt(k)).sort((a,b)=>a-b);
        timeKeys.forEach(t => {
          const list = eventsByTime[t] || [];
          const count = Math.max(DEFAULT_CAPACITY, list.length);
          for (let i=0;i<count;i++) {
            out.push({ time: t, ev: list[i] || null });
          }
        });
        return out;
      }
      // 枠情報がある場合は枠数に合わせて空行を出力
      const timeKeys = Object.keys(this.slotMap).map(k => parseInt(k)).sort((a,b)=>a-b);
      timeKeys.forEach(t => {
        const cap = Number(this.slotMap[t]) || 20;
        const list = eventsByTime[t] || [];
        const count = Math.max(cap, list.length);
        for (let i=0;i<count;i++) {
          out.push({ time: t, ev: list[i] || null });
        }
      });
      return out;
    }
  },
  async mounted() {
    // クエリ取得
    this.nendo = Number(this.$route.query.nendo) || '';
    this.onTheDay = Number(this.$route.query.onTheDay ?? -1);
    this.placeId = Number(this.$route.query.placeId ?? 0);
    this.splitByKeyword = String(this.$route.query.splitByKeyword ?? '0') !== '0';

    // 準備場所の取得（名称表示用）
    const placesSnap = await getDocs(collection(db, 'preparationPlaces'));
    this.places = placesSnap.docs.map(d => ({ id: d.id, ...d.data() }));
    if (this.placeId === 0) {
      this.placeName = '未設定';
    } else {
      const p = this.places.find(x => String(x.id) === String(this.placeId) || String(x.__id) === String(this.placeId));
      this.placeName = p?.name || '';
      // 年度別時間帯枠数
      this.slotMap = (p && p.timeSlots && this.nendo) ? (p.timeSlots[this.nendo] || null) : null;
    }

    await this.load();
    // 少し待ってから印刷開始（描画完了を待つ）
    setTimeout(() => window.print(), 300);
  },
  methods: {
    async load() {
      this.events = [];
      if (!this.nendo) return;
      let qy = query(collection(db, 'events'), where('celemony.year', '==', this.nendo));
      if (this.onTheDay !== -1) {
        qy = query(qy, where('celemony.onTheDay', '==', this.onTheDay));
      }
      if (this.placeId !== 0) {
        qy = query(qy, where('celemony.preparationPlace', '==', this.placeId));
      }
      const snap = await getDocs(qy);
      snap.docs.forEach(d => this.events.push({ id: d.id, __id: d.id, ...d.data() }));
    },
    getOnTheDayTime(ev) {
      return ev && ev.celemony ? ev.celemony.onTheDayTime : null;
    },
    getReceipt(ev) {
      return ev && ev.receipt ? ev.receipt : null;
    },
    getSlipNumber(ev) {
      const v = ev && ev.slipNumber ? ev.slipNumber : '';
      return this.toHalfWidthAscii(v).replace(/[‐‑‒–—―ーｰ−]/g, '-');
    },
    getEventTypeText(ev) {
      return ev && ev.eventType && ev.eventType.text ? ev.eventType.text : '';
    },
    getFurigana(ev) {
      return ev && ev.customer && ev.customer.furigana ? ev.customer.furigana : '';
    },
    getCustomerName(ev) {
      const name = ev && ev.customer && ev.customer.name ? ev.customer.name : '';
      return this.addSama(name);
    },
    getAddress(ev) {
      const a1 = ev && ev.customer && ev.customer.address1 ? ev.customer.address1 : '';
      const a2 = ev && ev.customer && ev.customer.address2 ? ev.customer.address2 : '';
      return a1 + a2;
    },
    getParentName(ev) {
      const name = ev && ev.customer && ev.customer.parentName ? ev.customer.parentName : '';
      return this.addSama(name);
    },
    getPlaceKeyword(ev) {
      return ev && ev.celemony && ev.celemony.place_keyword ? ev.celemony.place_keyword : '';
    },
    getPostal(ev) {
      const v = ev && ev.customer && ev.customer.postal ? ev.customer.postal : '';
      if (!v) return '';
      let s = this.toHalfWidthAscii(v).replace(/\s+/g, '').trim();
      if (/^\d{7}$/.test(s)) {
        s = s.slice(0, 3) + '-' + s.slice(3);
      }
      return '〒' + s;
    },
    getTelHome(ev) { return this.normalizePhone(ev && ev.customer ? ev.customer.telHome : ''); },
    getTelDaughter(ev) { return this.normalizePhone(ev && ev.customer ? ev.customer.telDaughter : ''); },
    getTelMother(ev) { return this.normalizePhone(ev && ev.customer ? ev.customer.telMother : ''); },
    getKingaku(ev) {
      return ev && ev.kingaku ? ev.kingaku : null;
    },
    getStaffName(ev) {
      return ev && ev.salesStaff && ev.salesStaff.name ? ev.salesStaff.name : '';
    },
    toTimeString(value) {
      if (value) {
        const str = ('0000' + value).slice(-4);
        return (str.slice(0, 2) + ':' + str.slice(2, 4)).replace(/^0+/, '');
      }
      return 'ー';
    },
    formatDate(v) {
      if (!v) return '';
      return moment(v).format('M/D');
    },
    formatMoney(v) {
      const n = Number(v);
      if (!Number.isFinite(n) || n <= 0) return '';
      return n.toLocaleString('ja-JP');
    },
    formatMemo(ev) {
      const a = ev && ev.celemony && ev.celemony.ontheDayDetail ? ev.celemony.ontheDayDetail : '';
      const b = ev && ev.remarks ? ev.remarks : '';
      return [a, b].filter(Boolean).join(' ');
    },
    toHalfWidthAscii(value) {
      if (value === null || value === undefined) return '';
      try {
        return moji(String(value)).convert('ZE', 'HE').toString();
      } catch (e) {
        return String(value);
      }
    },
    normalizePhone(value) {
      if (!value) return '';
      const src = this.toHalfWidthAscii(value).replace(/\s+/g, '').trim();
      const digits = src.replace(/[^0-9]/g, '');
      if (digits.length === 11) {
        // 携帯: 3-4-4
        return digits.slice(0, 3) + '-' + digits.slice(3, 7) + '-' + digits.slice(7);
      }
      if (digits.length === 10) {
        // 固定電話: 03/06 は 2-4-4、それ以外は 3-3-4 をデフォルトにする
        if (digits.startsWith('03') || digits.startsWith('06')) {
          return digits.slice(0, 2) + '-' + digits.slice(2, 6) + '-' + digits.slice(6);
        }
        return digits.slice(0, 3) + '-' + digits.slice(3, 6) + '-' + digits.slice(6);
      }
      // それ以外はダッシュを統一するのみ
      return src
        .replace(/[‐‑‒–—―ーｰ−]/g, '-')
        .trim();
    },
    addSama(name) {
      const s = (name || '').toString().trim();
      if (!s) return '';
      const already = ['様', 'さま', 'ｻﾏ', 'サマ'];
      if (already.some(sfx => s.endsWith(sfx))) return s;
      return s + ' 様';
    },
    
  },
};
</script>

<style scoped>
.print-root {
  padding: 8mm;
  color: #000;
  font-family: -apple-system, BlinkMacSystemFont, 'Hiragino Kaku Gothic ProN', Meiryo, sans-serif;
  font-size: 11px;
}
.print-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: auto;
}
.print-table thead { display: table-header-group; }
.print-table tr, .print-table th, .print-table td {
  border: 1px solid #000;
}
.print-table th, .print-table td {
  padding: 4px 6px;
  vertical-align: top;
}
.print-table thead th {
  font-size: 11px;
  text-align: center;
}
.title {
  text-align: left;
  font-size: 14px;
  border: none !important;
  padding: 0 0 6px 0 !important;
}
.col.time { width: 3%; }
.col.date { width: 5%; }
.col.slip { width: 6%; }
.col.type { width: 5%; }
.col.name { width: 15%; }
.col.parent { width: 7%; }
.col.address { width: 15%; }
.col.tel { width: 12%; }
.col.money { width: 5%; text-align: right; }
.col.memo { width: 21%; font-size:0.8em; }
.col.staff { width: 5%; }

.name .furigana { font-size: 10px; line-height: 1.1; }
.name .fullname { font-size: 14px; font-weight: 700; line-height: 1.2; }
.money { text-align: right; }
.time { font-weight: 700; font-size: 16px; }

/* 郵便番号の見た目 */
.address .postal { font-size: 10px; }

/* 時間が変わるごとに改ページ */
.break-before { break-before: page; }

@media print {
  @page { size: A4 landscape; margin: 10mm; }
  .print-root { padding: 0; }
  .break-before { page-break-before: always; }
}
</style>


